import { useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type SuggestComboboxProps = {
  label?: string
  id?: string
  placeholder?: string
  suggestions: string[]
  value: string | null | undefined
  onChange: (value: string) => void
  required?: boolean
  helpText?: string
  error?: string | string[]
}

export default function SuggestCombobox({
  label,
  id,
  placeholder = 'Type or pick...',
  suggestions,
  value,
  onChange,
  required = false,
  helpText,
  error,
}: SuggestComboboxProps) {
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const safeValue = value ?? ''
  const normalized = safeValue.trim().toLowerCase()
  const errorMessage = Array.isArray(error) ? error[0] : error

  const filtered = suggestions
    .filter(s => s.toLowerCase().includes(normalized))
    .sort((a, b) => {
      const aStarts = a.toLowerCase().startsWith(normalized) ? 0 : 1
      const bStarts = b.toLowerCase().startsWith(normalized) ? 0 : 1
      return aStarts === bStarts ? a.localeCompare(b) : aStarts - bStarts
    })
    .slice(0, 50)

  function selectItem(item: string) {
    onChange(item)
    setOpen(false)
    inputRef.current?.blur()
  }

  return (
    <div className="space-y-2">
      {label && (
        <Label htmlFor={id}>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      <Popover open={open && filtered.length > 0} onOpenChange={setOpen}>
        <PopoverAnchor asChild>
          <div ref={anchorRef}>
            <Input
              ref={inputRef}
              id={id}
              placeholder={placeholder}
              value={safeValue}
              aria-invalid={Boolean(errorMessage)}
              onChange={e => {
                onChange(e.target.value)
                if (!open) setOpen(true)
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  setOpen(false)
                }
              }}
            />
          </div>
        </PopoverAnchor>
        <PopoverContent
          onOpenAutoFocus={e => e.preventDefault()}
          onInteractOutside={e => {
            if (anchorRef.current?.contains(e.target as Node)) e.preventDefault()
          }}
          align="start"
          sideOffset={4}
          className="w-(--radix-popover-trigger-width) max-h-60 gap-0 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
        >
          {filtered.map(item => (
            <div
              key={item}
              className="cursor-default rounded-md px-1.5 py-1 text-sm select-none hover:bg-accent hover:text-accent-foreground"
              onMouseDown={e => e.preventDefault()}
              onClick={() => selectItem(item)}
            >
              {item}
            </div>
          ))}
        </PopoverContent>
      </Popover>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
      {errorMessage && <p className="text-xs text-destructive">{errorMessage}</p>}
    </div>
  )
}
